import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import { GtoConnectLockup } from '../brand/GtoConnectLockup';
import { BrandFrame } from '../brand/BrandFrame';
import { Yarlyk } from '../brand/Yarlyk';
import { Icon, type IconName } from '../components/Icon';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { WhenPicker, defaultWhen, toTimestamp, type When } from '../components/WhenPicker';
import { api, ApiError, type MeetingView } from '../lib/api';
import { formatWhen, participantsWord } from '../lib/format';
import type { Session } from '../lib/useSession';
import './LobbyScreen.css';

const FEATURES: { icon: IconName; text: string }[] = [
  { icon: 'people', text: 'До 50 участников' },
  { icon: 'link', text: 'Вход по ссылке' },
  { icon: 'calendar', text: 'Встречи по расписанию' },
  { icon: 'screen', text: 'Демонстрация экрана' },
];

export function LobbyScreen({
  session,
  onOpenRoom,
  onSignIn,
}: {
  session: Session;
  onOpenRoom: (code: string) => void;
  onSignIn: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const joinRef = useRef<HTMLInputElement>(null);

  function handleJoin(event: FormEvent) {
    event.preventDefault();
    setError('');

    // Просим именно код, но если вставили ссылку целиком — молча
    // достаём код из неё, а не заставляем вычищать вручную.
    const raw = code.trim();
    const normalized = (raw.includes('/') ? (raw.split(/[?#]/)[0].split('/').pop() ?? '') : raw)
      .toLowerCase()
      .replace(/\s+/g, '');

    if (!/^[a-z0-9]{3,12}(-[a-z0-9]{3,12}){0,3}$/.test(normalized)) {
      setError('Проверьте код: он выглядит как khtm-p8rq-x4wd');
      joinRef.current?.focus();
      return;
    }

    onOpenRoom(normalized);
  }

  return (
    <div className="lobby">
      <div className="grid-bg" />
      <div className="aura lobby__aura" />

      {/* Логотип живёт только в главном блоке, а вход — только на карточке.
          В шапке остаётся то, чего больше нигде нет: чья это сессия. */}
      {session.user && (
        <header className="lobby__header">
          <div className="lobby__account">
            <span className="lobby__email">{session.user.email}</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={session.signOut}>
              <Icon name="logout" size={16} />
              <span className="lobby__account-label">Выйти</span>
            </button>
          </div>
        </header>
      )}

      <main className="lobby__main">
        <section className="lobby__intro">
          <GtoConnectLockup markHeight={104} />

          <ul className="lobby__features">
            {FEATURES.map((feature) => (
              <li className="lobby__feature" key={feature.text}>
                <span className="lobby__feature-mark" aria-hidden>
                  <Icon name={feature.icon} size={19} />
                </span>
                {feature.text}
              </li>
            ))}
          </ul>
        </section>

        <section className="lobby__panel">
          <BrandFrame inset={-14} />

          <div className="card lobby__card">
            {session.loading ? (
              <div className="lobby__loading">
                <span className="spinner" />
              </div>
            ) : session.user ? (
              <CreatePanel onOpenRoom={onOpenRoom} />
            ) : (
              <SignInPrompt onSignIn={onSignIn} domain={session.domain} />
            )}

            <div className="rule">Присоединиться</div>

            <form className="lobby__join" onSubmit={handleJoin} noValidate>
              <label className="field">
                <span className="field__label">Код встречи</span>
                <input
                  ref={joinRef}
                  className="input lobby__join-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="khtm-p8rq-x4wd"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-invalid={Boolean(error)}
                />
              </label>
              <button type="submit" className="btn btn--ghost btn--lg" disabled={!code.trim()}>
                <Icon name="arrowRight" />
                <span className="visually-hidden">Присоединиться</span>
              </button>
            </form>

            {error && (
              <p className="lobby__error" role="alert">
                <Icon name="alert" size={16} />
                {error}
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

// ── Гостю: объяснить, что вход нужен только для создания ──────────────

function SignInPrompt({ onSignIn, domain }: { onSignIn: () => void; domain: string }) {
  return (
    <div className="lobby__prompt">
      <h2 className="lobby__card-title">Создать встречу</h2>
      <p className="lobby__prompt-text">
        Создавать встречи могут сотрудники GTO GROUP. Войдите по почте <b>@{domain}</b> — код
        придёт письмом.
      </p>
      <button type="button" className="btn btn--primary btn--lg btn--block" onClick={onSignIn}>
        <Icon name="shield" />
        Войти по рабочей почте
      </button>
      <p className="lobby__prompt-note">
        Чтобы присоединиться к чужой встрече, вход не нужен — достаточно ссылки.
      </p>
    </div>
  );
}

// ── Сотруднику: мгновенная встреча, планирование, свои встречи ────────

function CreatePanel({ onOpenRoom }: { onOpenRoom: (code: string) => void }) {
  const [mode, setMode] = useState<'instant' | 'scheduled'>('instant');
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState<When>(defaultWhen);
  const [duration, setDuration] = useState(60);
  const titleRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mine, setMine] = useState<MeetingView[]>([]);
  const [created, setCreated] = useState<MeetingView | null>(null);

  const loadMine = useCallback(async () => {
    try {
      const data = await api.myMeetings();
      setMine(data.meetings.filter((m) => m.scheduled));
    } catch {
      setMine([]);
    }
  }, []);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setError('');

    const name = title.trim();
    if (!name) {
      setError('Назовите встречу — по названию её узнают в приглашении');
      titleRef.current?.focus();
      return;
    }

    setBusy(true);

    try {
      if (mode === 'instant') {
        const { meeting } = await api.createMeeting({ title: name });
        onOpenRoom(meeting.code);
        return;
      }

      const { meeting } = await api.createMeeting({
        title: name,
        startsAt: toTimestamp(when),
        durationMinutes: duration,
      });

      setCreated(meeting);
      setTitle('');
      await loadMine();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать встречу');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(code: string) {
    await api.cancelMeeting(code).catch(() => {});
    setCreated((current) => (current?.code === code ? null : current));
    await loadMine();
  }

  return (
    <div className="lobby__create">
      <h2 className="lobby__card-title">Создать встречу</h2>

      {/* Ярлыки-переключатели: плоской стороной к нижней грани. */}
      <div className="lobby__modes" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'instant'}
          className={`lobby__mode ${mode === 'instant' ? 'lobby__mode--on' : ''}`}
          onClick={() => setMode('instant')}
        >
          Сейчас
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'scheduled'}
          className={`lobby__mode ${mode === 'scheduled' ? 'lobby__mode--on' : ''}`}
          onClick={() => setMode('scheduled')}
        >
          По расписанию
        </button>
      </div>

      {/* noValidate: нативная проверка браузера перехватывает отправку
          раньше обработчика и показывает свой пузырёк вместо наших
          сообщений. Атрибут required оставлен для доступности. */}
      <form className="lobby__form" onSubmit={create} noValidate>
        <label className="field">
          <span className="field__label">Название встречи</span>
          <input
            ref={titleRef}
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Планёрка отдела"
            maxLength={120}
            required
            aria-invalid={Boolean(error) && !title.trim()}
          />
        </label>

        {mode === 'scheduled' && (
          <div className="when">
            <div className="field">
              <span className="field__label">Когда</span>
              <WhenPicker value={when} onChange={setWhen} durationMinutes={duration} />
            </div>

            <div className="field">
              <span className="field__label">Длительность</span>
              <div className="when__durations">
                {DURATIONS.map((option) => (
                  <button
                    key={option.minutes}
                    type="button"
                    className={`when__slot ${duration === option.minutes ? 'when__slot--on' : ''}`}
                    onClick={() => setDuration(option.minutes)}
                    aria-pressed={duration === option.minutes}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && (
          <p className="lobby__error" role="alert">
            <Icon name="alert" size={16} />
            {error}
          </p>
        )}

        <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={busy}>
          {busy ? <span className="spinner" /> : <Icon name={mode === 'instant' ? 'plus' : 'calendar'} />}
          {busy ? 'Создаём' : mode === 'instant' ? 'Начать сейчас' : 'Запланировать'}
        </button>
      </form>

      {created && (
        <div className="lobby__created" role="status">
          <Yarlyk side="bottom" tone="accent">
            Встреча создана
          </Yarlyk>
          <p className="lobby__created-title">{created.title}</p>
          <p className="lobby__created-when">{formatWhen(created.startsAt)}</p>
          <CopyLinkButton code={created.code} label="Скопировать ссылку" />
        </div>
      )}

      {mine.length > 0 && (
        <div className="lobby__mine">
          <span className="eyebrow">Запланировано</span>
          <ul className="lobby__mine-list">
            {mine.map((meeting) => (
              <li className="lobby__mine-item" key={meeting.code}>
                <div className="lobby__mine-info">
                  <span className="lobby__mine-title">{meeting.title}</span>
                  <span className="lobby__mine-when">
                    {formatWhen(meeting.startsAt)}
                    {meeting.state === 'open' &&
                      ` · идёт, ${meeting.participants ?? 0} ${participantsWord(meeting.participants ?? 0)}`}
                  </span>
                </div>
                <div className="lobby__mine-actions">
                  <CopyLinkButton code={meeting.code} label="" variant="quiet" />
                  <button
                    type="button"
                    className="lobby__mine-cancel"
                    onClick={() => cancel(meeting.code)}
                    title="Отменить встречу"
                    aria-label={`Отменить встречу «${meeting.title}»`}
                  >
                    <Icon name="close" size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Длительности ──────────────────────────────────────────────────────

const DURATIONS = [
  { minutes: 30, label: '30 мин' },
  { minutes: 60, label: '1 час' },
  { minutes: 90, label: '1,5 часа' },
  { minutes: 120, label: '2 часа' },
  { minutes: 240, label: '4 часа' },
  { minutes: 480, label: '8 часов' },
];
